import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { townsQuery } from '@/features/catalog/api'
import { useTownStore } from '@/features/town/store'
import { NEAR_TOWN_KM, nearestTown } from '@/lib/geo'
import type { Town } from '@/types/api'

import { locate, type LocateError } from './geolocate'
import { useLocationStore } from './store'

export type LocateStatus = 'idle' | 'locating' | LocateError

/** What "Use my location" found: the nearest town, or none within reach (BR-24). */
export type LocateResult = { town: Town | null }

export const LOCATE_ERRORS: Record<LocateError, string> = {
  denied: 'Location is blocked for this site. Allow it in your browser settings to see distances.',
  unavailable: 'Couldn’t get your location. Try again outdoors or later.',
  unsupported: 'This browser can’t share your location.',
}

/** "Showing Wandoor, the town nearest to you." (BR-24) */
export const foundText = ({ town }: LocateResult) =>
  town
    ? `Showing ${town.name}, the town nearest to you.`
    : `townshop.in isn’t in any town within ${NEAR_TOWN_KM} km of you yet. Showing all towns.`

/**
 * "Use my location" (D-05: only on a tap). Saves the position on the device for distances (BR-4)
 * and switches to the nearest town, or "All towns" when none is near (BR-24).
 */
export function useLocateMe() {
  const qc = useQueryClient()
  const setCoords = useLocationStore((s) => s.setCoords)
  const setTown = useTownStore((s) => s.setTown)
  const [status, setStatus] = useState<LocateStatus>('idle')

  const run = async (): Promise<LocateResult | null> => {
    setStatus('locating')
    try {
      const coords = await locate()
      setCoords(coords)
      const towns = await qc.ensureQueryData(townsQuery).catch(() => [])
      const town = nearestTown(coords, towns)
      setTown(town?.slug ?? null, true)
      setStatus('idle')
      return { town }
    } catch (e) {
      setStatus(e as LocateError)
      return null
    }
  }

  return { status, run }
}
